import {
  Card,
  createStyles,
  Divider,
  MantineSize,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { ReactNode } from 'react';
import { AuditLogActions } from '../../../enums/retrieve-audit-logs.enum';
import {
  BasicAuditLog,
  DetailedAuditLog,
} from '../../../type/retrieve-audit-logs.types';
import { getEntityType } from './logs-details-section-table-rows.component';

interface Props {
  selectedLog: BasicAuditLog;
}
function LogsChanges(props: Props) {
  const { selectedLog } = props;
  const { classes } = useStyles();

  const SmallText = ({ children }: { children: ReactNode }) => {
    return <Text size="sm">{children}</Text>;
  };

  const getField = (detail: DetailedAuditLog, index?: number) => {
    let data: string | undefined = '';

    if (
      selectedLog.logActionId === AuditLogActions.UPDATE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.UPDATE_ACCOUNT ||
      selectedLog.logActionId === AuditLogActions.UPDATE_POS ||
      selectedLog.logActionId === AuditLogActions.UPDATE_MID ||
      selectedLog.logActionId === AuditLogActions.ENABLE_MID ||
      selectedLog.logActionId === AuditLogActions.DISABLE_MID ||
      selectedLog.logActionId === AuditLogActions.ASSIGN_MID
    ) {
      data = detail.field;
    } else if (
      selectedLog.logActionId === AuditLogActions.APPROVE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.DECLINE_ONBOARDING
    ) {
      data = 'Onboarding status';
    } else if (
      selectedLog.logActionId === AuditLogActions.AUB_APPROVE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.AUB_DECLINE_ONBOARDING
    ) {
      data = 'AUB onboarding status';
    } else if (
      selectedLog.logActionId === AuditLogActions.UB_APPROVE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.UB_DECLINE_ONBOARDING
    ) {
      data = 'Unionbank onboarding status';
    } else if (
      selectedLog.logActionId === AuditLogActions.MAYA_APPROVE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.MAYA_DECLINE_ONBOARDING
    ) {
      data = 'Maya onboarding status';
    } else if (
      selectedLog.logActionId === AuditLogActions.ENABLE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.ENABLE_ACCOUNT ||
      selectedLog.logActionId === AuditLogActions.ENABLE_ENTITY ||
      selectedLog.logActionId === AuditLogActions.DISABLE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.DISABLE_ACCOUNT ||
      selectedLog.logActionId === AuditLogActions.DISABLE_ENTITY
    ) {
      data = 'Active status';
    }

    return data;
  };

  const getTarget = (detail: DetailedAuditLog): string | undefined => {
    let data: string | undefined = '';
    if (
      selectedLog.logActionId === AuditLogActions.ENABLE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.ENABLE_ENTITY ||
      selectedLog.logActionId === AuditLogActions.DISABLE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.DISABLE_ENTITY
    ) {
      data = detail.targetMerchantName;
    } else if (
      selectedLog.logActionId === AuditLogActions.ENABLE_ACCOUNT ||
      selectedLog.logActionId === AuditLogActions.DISABLE_ACCOUNT
    ) {
      data = detail.targetUsername;
    } else if (selectedLog.logActionId === AuditLogActions.ASSIGN_MID) {
      data = detail.midName;
    }
    return data;
  };

  const getChangeMessage = () => {
    let data: JSX.Element = <></>;

    if (
      selectedLog.logActionId === AuditLogActions.UPDATE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.UPDATE_ACCOUNT ||
      selectedLog.logActionId === AuditLogActions.UPDATE_POS ||
      selectedLog.logActionId === AuditLogActions.UPDATE_MID ||
      selectedLog.logActionId === AuditLogActions.ENABLE_MID ||
      selectedLog.logActionId === AuditLogActions.DISABLE_MID ||
      selectedLog.logActionId === AuditLogActions.AUB_APPROVE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.AUB_DECLINE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.UB_APPROVE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.UB_DECLINE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.MAYA_APPROVE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.MAYA_DECLINE_ONBOARDING
    ) {
      selectedLog.details.forEach((detail, index) => {
        data = (
          <>
            {data}
            <tr>
              <td>
                <SmallText>
                  {index === 0 ? detail.targetMerchantName : ''}
                </SmallText>
              </td>
              <td>
                <SmallText>{getField(detail)}</SmallText>
              </td>
              <td>
                <div>
                  {detail.field === 'Mode of Payment' &&
                  !detail.new &&
                  detail.original ? (
                    <SmallText>
                      Removed{' '}
                      <SafeText data={detail.original} weight="bolder" />
                    </SmallText>
                  ) : detail.field === 'Mode of Payment' &&
                    detail.new &&
                    !detail.original ? (
                    <SmallText>
                      Added <SafeText data={detail.new} weight="bolder" />
                    </SmallText>
                  ) : (
                    <SmallText>
                      Changed from{' '}
                      <strong>
                        <SafeText data={detail.original} weight="bolder" />
                      </strong>{' '}
                      to{' '}
                      <strong>
                        <SafeText data={detail.new} weight="bolder" />
                      </strong>
                    </SmallText>
                  )}
                </div>
              </td>
            </tr>
          </>
        );
      });
    } else if (
      selectedLog.logActionId === AuditLogActions.APPROVE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.DECLINE_ONBOARDING
    ) {
      selectedLog.details.forEach((detail, index) => {
        data = (
          <>
            {data}
            <tr>
              <td>
                <SmallText>{detail.targetMerchantName}</SmallText>
              </td>
              <td>
                <SmallText>Onboarding status</SmallText>
              </td>
              <td>
                <SmallText>
                  Changed from <strong>{detail.original}</strong> to{' '}
                  <strong>{detail.new}</strong>
                </SmallText>
              </td>
            </tr>
          </>
        );
      });
    } else if (
      selectedLog.logActionId === AuditLogActions.ENABLE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.ENABLE_ACCOUNT ||
      selectedLog.logActionId === AuditLogActions.ENABLE_ENTITY ||
      selectedLog.logActionId === AuditLogActions.DISABLE_ONBOARDING ||
      selectedLog.logActionId === AuditLogActions.DISABLE_ACCOUNT ||
      selectedLog.logActionId === AuditLogActions.DISABLE_ENTITY
    ) {
      selectedLog.details.forEach((detail, index) => {
        data = (
          <>
            {data}
            <tr>
              <td>{getTarget(detail)}</td>
              <td>{getField(detail)}</td>
              <td>
                Changed from <strong>{detail.original}</strong> to{' '}
                <strong>{detail.new}</strong>
              </td>
            </tr>
          </>
        );
      });
    } else if (selectedLog.logActionId === AuditLogActions.ASSIGN_MID) {
      selectedLog.details.forEach((detail, index) => {
        data = (
          <>
            {data}
            {detail.posNames?.map((posName) => {
              return [
                <tr>
                  <td>{getTarget(detail)}</td>
                  <td>{posName}</td>
                  <td>
                    Assigned <strong>{detail.midName}</strong> to{' '}
                    <strong>{posName}</strong>
                  </td>
                </tr>,
              ];
            })}
          </>
        );
      });
    }

    return data;
  };

  return (
    <>
      {selectedLog.logActionId !== AuditLogActions.CREATE_PAYMENT_LINK && (
        <Card withBorder p="xl" sx={{ width: '100%' }}>
          <Stack>
            <Title order={4}>Changes</Title>
            <Divider />
            {/* <ScrollArea style={{ height: 250 }}> */}
            <div className={classes.tableContainer}>
              <Table>
                <thead className={classes.changes}>
                  <tr>
                    <th>{'Target ' + getEntityType(selectedLog)}</th>
                    <th>Field</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>{getChangeMessage() || <tr></tr>}</tbody>
              </Table>
            </div>
            {/* </ScrollArea> */}
          </Stack>
        </Card>
      )}
    </>
  );
}

export default LogsChanges;

const SafeText = ({
  data,
  size,
  weight,
}: {
  data?: string | null;
  size?: MantineSize;
  weight?: number | string;
}) => {
  return data && data !== 'N/A' && data !== '.' ? (
    <>
      <Text
        size={size ? size : 'sm'}
        weight={typeof weight === 'string' ? parseInt(weight) : weight}
        component="span"
      >
        {data}
      </Text>
    </>
  ) : (
    <Text
      size="sm"
      color="dimmed"
      weight={typeof weight === 'string' ? parseInt(weight) : weight}
      component="span"
    >
      N/A
    </Text>
  );
};

const useStyles = createStyles((theme, _params, getRef) => {
  return {
    logsChanges: {
      whiteSpace: 'nowrap',
    },
    tableContainer: {
      whiteSpace: 'nowrap',
      overflowX: 'auto',
      overflowY: 'auto',
      maxHeight: '350px',
    },
    changes: {
      backgroundColor: '#f6f8fb',
      color: '#223354',
    },
  };
});
